import { useHoverPopoverClose } from '@app/components/base/HoverPopover';
import { LTime } from '@app/components/date/LTime';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { copyUrlTooltipMessage } from '@app/modules/logger/screens/stream/components/focus/cursor/cursor-menu/CursorMenuP';
import { useCopyCursorToClipboard } from '@app/modules/logger/screens/stream/helpers/useCopyCursorToClipboard';
import { Timestamp, toDateTimeStr } from '@mi-front/core';
import LinkIcon from '@mui/icons-material/Link';
import { IconButton, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type CopyLinkAtTimestampButtonProps = {
  /** Absolute timestamp included in the copied logger URL. */
  time: Timestamp;
  disabled?: boolean;
  /** Called after copy; defaults to closing the HoverPopover. */
  onDone?: () => void;
};

/**
 * Shared stream popup action: copy a logger link with cursor timestamp.
 * When disabled, tooltip omits the formatted time (matches Live Topics behavior).
 */
export function CopyLinkAtTimestampButton({
  time,
  disabled,
  onDone,
}: CopyLinkAtTimestampButtonProps) {
  const { $t } = useIntl();
  const station = useActiveStation();
  const copyCursorToClipboard = useCopyCursorToClipboard();
  const closePopover = useHoverPopoverClose();

  const handleCopyLink = useCallback(() => {
    copyCursorToClipboard(station, time);
    (onDone ?? closePopover)?.();
  }, [station, time, copyCursorToClipboard, onDone, closePopover]);

  return (
    <Tooltip
      title={
        disabled ? (
          $t(copyUrlTooltipMessage, { value: '' })
        ) : (
          <FormattedMessage
            {...copyUrlTooltipMessage}
            values={{
              value: <LTime value={toDateTimeStr(time)} hour12={false} showSeconds />,
            }}
          />
        )
      }
      placement="top"
    >
      <IconButton size="small" onClick={handleCopyLink} disabled={disabled}>
        <LinkIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
